import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShareNetwork, ClipboardText, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fetchNifcIncidents } from "../lib/api";
import { shareOrigin } from "../lib/shareOrigin";
import { buildIncidentCaption, evacsForShare } from "../lib/shareCaption";
import { sameFire } from "../lib/dedupe";
import InlineShareButton from "../components/InlineShareButton";

export default function SharePreviewPage() {
  const { id } = useParams();
  const [inc, setInc] = useState(null);
  const [error, setError] = useState(null);
  const [evacAlerts, setEvacAlerts] = useState([]);
  const [nifcMatch, setNifcMatch] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [lastSharedAt, setLastSharedAt] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/incidents/${id}`)
      .then((r) => {
        setInc(r.data);
        if (r.data?.latitude != null) {
          fetchNifcIncidents({ active_only: true })
            .then((nifc) => setNifcMatch((nifc || []).find((n) => sameFire(r.data, n)) || null))
            .catch(() => {});
        }
      })
      .catch(() => setError("Incident not found."));
    api.get("/evac-alerts").then((r) => setEvacAlerts(r.data || [])).catch(() => {});
    api.get(`/incidents/${id}/ai-summary`, { params: { cached_only: true } })
      .then((r) => { if (r.data?.summary) setAiSummary(r.data.summary); })
      .catch(() => {});
    api.get(`/metrics/share-log/${id}`)
      .then((r) => setLastSharedAt(r.data.last_shared_at || null))
      .catch(() => {});
  }, [id]);

  const shareUrl = `${shareOrigin()}/api/share/incident/${id}`;
  const caption = useMemo(() => {
    if (!inc) return "";
    return buildIncidentCaption({
      name: inc.name,
      county: inc.county,
      acres: inc.acres ?? nifcMatch?.incident_size,
      contained: nifcMatch?.percent_contained,
      statusLabel: inc.status_label,
      incNum: inc.inc_num,
      resources: inc.resources,
      evacs: evacsForShare(evacAlerts, inc.latitude, inc.longitude, inc.name),
      updatedAt: inc.date,
      lastSharedAt,
      summary: aiSummary,
      url: shareUrl,
    });
  }, [inc, nifcMatch, evacAlerts, aiSummary, lastSharedAt, shareUrl]);

  const copyCaption = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(caption).catch(() => {});
    setCopied(true);
    toast.success("Post copied — paste it anywhere");
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="min-h-screen bg-tac-bg text-tac-text px-4 py-10">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 text-tac-primary mb-1">
          <ShareNetwork size={16} weight="fill" />
          <span className="text-[12px] font-bold uppercase tracking-[0.2em]">Share Preview</span>
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-1" data-testid="share-preview-title">
          {inc?.name || "Incident"}
        </h1>
        <div className="text-[13px] font-mono-tac text-tac-faint mb-6">
          This is exactly what will be posted when you share — review it, then pick where to share.
        </div>

        {error && (
          <div className="border border-tac-danger/50 bg-tac-danger/10 p-4 text-sm text-tac-danger" data-testid="share-preview-error">
            {error}
          </div>
        )}
        {!error && !inc && (
          <div className="border border-tac-border bg-tac-surface/40 p-6 text-sm text-tac-dim animate-pulse" data-testid="share-preview-loading">
            Building your post preview…
          </div>
        )}
        {inc && (
          <pre
            data-testid="share-preview-body"
            className="border border-tac-border bg-tac-surface/40 p-5 text-[14px] leading-relaxed text-tac-text whitespace-pre-wrap break-words font-sans rounded-sm"
          >
            {caption}
          </pre>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <InlineShareButton
            testid="share-preview-share-button"
            url={shareUrl}
            title={`${inc?.name || "Incident"} — Firewatch`}
            caption={caption}
            text={`${inc?.name || "Incident"} — live incident details via Okanogan Highlands Firewatch`}
          />
          <button
            type="button"
            data-testid="share-preview-copy-caption"
            onClick={copyCaption}
            disabled={!inc}
            className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-bold uppercase tracking-wider border border-tac-border rounded-sm text-tac-dim hover:text-tac-text hover:border-tac-borderBright transition-colors disabled:opacity-50"
          >
            {copied ? <Check size={13} weight="bold" className="text-tac-safe" /> : <ClipboardText size={13} weight="bold" />}
            {copied ? "Copied" : "Copy Post Text"}
          </button>
          <Link
            to={`/incident/${id}`}
            data-testid="share-preview-open-incident"
            className="text-[12px] font-bold uppercase tracking-wider text-tac-primary hover:underline"
          >
            Full incident page →
          </Link>
        </div>
      </div>
    </div>
  );
}
